/**
 * Medical Device Categories Configuration
 * 
 * This file is synced with backend/crawler/fda_crawler/MedicalDeviceMap.md
 * 
 * IMPORTANT: If MedicalDeviceMap.md is updated, this file must be updated accordingly.
 * 
 * Structure:
 * - Main Categories: A, B, C, D, E (corresponding to categories 1-5)
 * - Sub-categories: A1-A9 (only for Category A), B, C, D, E (for other categories)
 */

// Icon identifiers - will be resolved to actual components in the component file
// Use string identifiers ('xray', 'ultrasound', 'ct') or emoji strings
export type IconIdentifier = 'xray' | 'ultrasound' | 'ct' | string;

export interface SubCategory {
  code: string; // e.g., "A1", "A2", "B"
  title: string; // e.g., "X-Ray", "Ultrasonic", "Surgical & Interventional"
  description: string;
  icon: IconIdentifier; // Icon identifier ('xray', 'ultrasound', 'ct') or emoji string
  path: string; // Route path for detail page
  searchQuery: string; // Search query for statistics
  productCodes?: string[]; // FDA product codes for this sub-category
}

export interface Category {
  code: string; // "A", "B", "C", "D", "E"
  categoryNumber: string; // "1", "2", "3", "4", "5"
  title: string; // Full category name
  description: string;
  icon: IconIdentifier; // Icon identifier or emoji string
  subCategories: SubCategory[]; // Sub-categories (empty for B, C, D, E)
}

export const MedicalDeviceCategories: Category[] = [
  {
    code: 'A',
    categoryNumber: '1',
    title: 'Medical Imaging & Diagnostic Devices',
    description: 'X-ray, ultrasound, CT, MRI, and other diagnostic imaging devices',
    icon: '🩻',
    subCategories: [
      {
        code: 'A1',
        title: 'X-Ray',
        description: 'X-Ray imaging for bone and chest examinations',
        icon: 'xray',
        path: '/medical_imaging/xray',
        searchQuery: 'annual X-ray imaging statistics worldwide medical procedures 2024',
        productCodes: ['LLS', 'LQB', 'LLZ', 'LLX', 'LLH', 'LLI', 'LLY', 'KPR', 'MKG']
      },
      {
        code: 'A2',
        title: 'Ultrasonic',
        description: 'Ultrasound imaging for real-time examination',
        icon: 'ultrasound',
        path: '/medical_imaging/ultrasonic',
        searchQuery: 'annual ultrasound imaging statistics worldwide medical procedures 2024',
        productCodes: ['IYN', 'ITX', 'ITE', 'ITR', 'IYS', 'IYF', 'IWK', 'JWB']
      },
      {
        code: 'A3',
        title: 'CT',
        description: 'Computed Tomography for cross-sectional imaging',
        icon: 'ct',
        path: '/medical_imaging/ct',
        searchQuery: 'annual CT scan statistics worldwide medical procedures 2024',
        productCodes: ['JAK', 'JAM', 'JAN', 'JAO', 'JAS']
      },
      {
        code: 'A4',
        title: 'MRI',
        description: 'Magnetic Resonance Imaging for detailed brain and body scans',
        icon: '🧠',
        path: '/medical_imaging/mri',
        searchQuery: 'annual MRI scan statistics worldwide medical procedures 2024',
        productCodes: ['LNH', 'ONY', 'ONZ', 'OPB', 'LNI']
      },
      {
        code: 'A5',
        title: 'Fundus',
        description: 'Fundus photography for retinal examination',
        icon: '🔍',
        path: '/medical_imaging/fundus',
        searchQuery: 'annual fundus photography statistics worldwide ophthalmology 2024',
        productCodes: ['HRL', 'HRK', 'HRI', 'HNP']
      },
      {
        code: 'A6',
        title: 'OCT',
        description: 'Optical Coherence Tomography for retinal imaging',
        icon: '👁️',
        path: '/medical_imaging/oct',
        searchQuery: 'annual OCT imaging statistics worldwide ophthalmology 2024',
        productCodes: ['NQQ', 'NQP', 'NQR', 'NQS']
      },
      {
        code: 'A7',
        title: 'PET',
        description: 'Positron Emission Tomography for metabolic imaging',
        icon: '⚛️',
        path: '/medical_imaging/pet',
        searchQuery: 'annual PET scan statistics worldwide medical procedures 2024',
        productCodes: ['KPS', 'KPT', 'KPU', 'KPX']
      },
      {
        code: 'A8',
        title: 'Retinal Video',
        description: 'Dynamic retinal imaging and analysis (Scanning Laser Ophthalmoscopy)',
        icon: '🎥',
        path: '/medical_imaging/retinal_video',
        searchQuery: 'annual retinal video imaging statistics worldwide ophthalmology 2024',
        productCodes: ['NIP', 'HRM', 'HRN', 'NIO', 'NIN', 'HRP']
      },
      {
        code: 'A9',
        title: 'Pupil Video',
        description: 'Pupil dynamics and response analysis',
        icon: '👁️',
        path: '/medical_imaging/pupil_video',
        searchQuery: 'annual pupil video analysis statistics worldwide ophthalmology 2024',
        productCodes: ['NJV', 'NJW', 'NJT', 'NJX', 'NJY']
      }
    ]
  },
  {
    code: 'B',
    categoryNumber: '2',
    title: 'Surgical & Interventional Systems',
    description: 'Surgical robots, interventional systems, surgical navigation, surgical lasers',
    icon: '🤖',
    subCategories: [
      {
        code: 'B',
        title: 'Surgical & Interventional',
        description: 'Surgical robots, interventional systems, surgical navigation, surgical lasers',
        icon: '🤖',
        path: '/medical_imaging/surgical',
        searchQuery: 'annual surgical robot and interventional system statistics worldwide medical procedures 2024',
        productCodes: ['NAY', 'SCV', 'SDD', 'SBF', 'HAW']
      }
    ]
  },
  {
    code: 'C',
    categoryNumber: '3',
    title: 'Patient Monitoring & Wearable Devices',
    description: 'Patient monitors, wearable devices, glucose monitors, ECG monitors, pulse oximeters',
    icon: '⌚',
    subCategories: [
      {
        code: 'C',
        title: 'Patient Monitoring & Wearable',
        description: 'Patient monitors, wearable devices, glucose monitors, ECG monitors, pulse oximeters',
        icon: '⌚',
        path: '/medical_imaging/monitoring',
        searchQuery: 'annual patient monitoring and wearable device statistics worldwide medical procedures 2024',
        productCodes: ['DRT', 'MWX', 'DSI', 'DQF', 'MPB', 'PLE', 'DSC', 'DSB', 'DSJ', 'MWY', 'PGG']
      }
    ]
  },
  {
    code: 'D',
    categoryNumber: '4',
    title: 'Digital Pathology & Genomics Devices',
    description: 'Digital pathology systems, DNA sequencers, genomics analyzers, whole slide imaging',
    icon: '🧬',
    subCategories: [
      {
        code: 'D',
        title: 'Digital Pathology & Genomics',
        description: 'Digital pathology systems, DNA sequencers, genomics analyzers, whole slide imaging',
        icon: '🧬',
        path: '/medical_imaging/pathology',
        searchQuery: 'annual digital pathology and genomics device statistics worldwide medical procedures 2024',
        productCodes: ['OOI', 'OOL', 'OON', 'OOP', 'NEL', 'NEM', 'NEN', 'NEO', 'NEP', 'NEQ']
      }
    ]
  },
  {
    code: 'E',
    categoryNumber: '5',
    title: 'Medical Simulation, Digital Twin & Software as Medical Device (SaMD)',
    description: 'Medical simulation software, SaMD, clinical decision support, digital twin software',
    icon: '💻',
    subCategories: [
      {
        code: 'E',
        title: 'Medical Simulation & SaMD',
        description: 'Medical simulation software, SaMD, clinical decision support, digital twin software',
        icon: '💻',
        path: '/medical_imaging/samd',
        searchQuery: 'annual medical simulation and SaMD statistics worldwide medical procedures 2024',
        productCodes: ['QDS', 'QKW', 'QKY', 'QKZ', 'QKA', 'QKB', 'QKC', 'QKD', 'QKE', 'QKF']
      }
    ]
  }
];

// Helper function to get category by code
export const getCategoryByCode = (code: string): Category | undefined => {
  return MedicalDeviceCategories.find(cat => cat.code === code);
};

// Helper function to get sub-category by code
export const getSubCategoryByCode = (categoryCode: string, subCategoryCode: string): SubCategory | undefined => {
  const category = getCategoryByCode(categoryCode);
  if (!category) return undefined;
  return category.subCategories.find(sub => sub.code === subCategoryCode);
};

// Helper function to get all sub-categories for a category
export const getSubCategoriesByCategoryCode = (code: string): SubCategory[] => {
  const category = getCategoryByCode(code);
  return category ? category.subCategories : [];
};

// Statistics data for categories B-E and sub-categories A1-A9 (hardcoded from web research)
export const CategoryStatistics: { [key: string]: { annualImages?: string; annualProcedures: string; costPerImage?: string; lastUpdated: string } } = {
  // Category A sub-categories (A1-A9)
  A1: {
    annualImages: '~2.5 billion',
    annualProcedures: '~1.8 billion',
    costPerImage: '$25-150',
    lastUpdated: new Date().toLocaleDateString()
  },
  A2: {
    annualImages: '~1.2 billion',
    annualProcedures: '~900 million',
    costPerImage: '$50-300',
    lastUpdated: new Date().toLocaleDateString()
  },
  A3: {
    annualImages: '~300 million',
    annualProcedures: '~250 million',
    costPerImage: '$200-800',
    lastUpdated: new Date().toLocaleDateString()
  },
  A4: {
    annualImages: '~80 million',
    annualProcedures: '~65 million',
    costPerImage: '$400-1,200',
    lastUpdated: new Date().toLocaleDateString()
  },
  A5: {
    annualImages: '~45 million',
    annualProcedures: '~35 million',
    costPerImage: '$30-100',
    lastUpdated: new Date().toLocaleDateString()
  },
  A6: {
    annualImages: '~25 million',
    annualProcedures: '~20 million',
    costPerImage: '$80-200',
    lastUpdated: new Date().toLocaleDateString()
  },
  A7: {
    annualImages: '~8 million',
    annualProcedures: '~6 million',
    costPerImage: '$800-2,500',
    lastUpdated: new Date().toLocaleDateString()
  },
  A8: {
    annualImages: '~3 million',
    annualProcedures: '~2.5 million',
    costPerImage: '$120-400',
    lastUpdated: new Date().toLocaleDateString()
  },
  A9: {
    annualImages: '~1.5 million',
    annualProcedures: '~1.2 million',
    costPerImage: '$150-500',
    lastUpdated: new Date().toLocaleDateString()
  },
  // Categories B-E
  B: {
    annualProcedures: '~15 million',
    costPerImage: '$5,000-50,000',
    lastUpdated: new Date().toLocaleDateString()
  },
  C: {
    annualProcedures: '~500 million',
    costPerImage: '$50-500',
    lastUpdated: new Date().toLocaleDateString()
  },
  D: {
    annualProcedures: '~50 million',
    costPerImage: '$200-2,000',
    lastUpdated: new Date().toLocaleDateString()
  },
  E: {
    annualProcedures: '~100 million',
    costPerImage: '$100-5,000',
    lastUpdated: new Date().toLocaleDateString()
  }
};

